import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Stack, Group, Text, Button, Card, Box } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { useNavigate } from '@tanstack/react-router';
import { IconPlus, IconListTree } from '@tabler/icons-react';
import type { ListlistDto } from '@lib2/model/listlist/ListlistDto';
import { ListlistTypeId } from '@lib2/model/listlist/ListlistType';
import type { ChildListlistOrListId } from '@lib2/model/listlist/ListlistReq';
import { useAddChild, useAddChildren, useRemoveChild, useSaveChildOrder } from '../useAppContentMutations';
import {
  AppContentUtils,
  CATEGORY_ADD_LABEL,
  CHILD_KIND_COLORS,
  CHILD_KIND_LABELS,
  type CategoryChildView,
  type ChildKind,
} from '../AppContentUtils';
import { SortableChildItem } from './SortableChildItem';
import { AddChildDialog } from './AddChildDialog';
import { AddToCategoryDialog, type AddPosition } from './AddToCategoryDialog';
import { HomeRowPreview } from './HomeRowPreview';

interface OrderedChildrenEditorProps {
  parentLlId: number;
  parentType: ListlistTypeId;
  /** The parent's children in the operator's ONE order (server position order). */
  orderedChildren: ChildListlistOrListId[];
  /** Listlists that can be added as children (categories under a root, series under a category). */
  allAvailableChildren: ListlistDto[];
  childLookup: Map<number, ListlistDto>;
  childDetailRoute: string;
  /**
   * Category parents only (design ADR-0014): every child resolved for
   * display — series with their art and season line, videos/streams with
   * their poster frame, kind, and on-air state — keyed by childKey.
   */
  childViews?: Map<string, CategoryChildView>;
  /** Category parents only: llId → season count for the picker's series rows. */
  seasonCountByLlId?: Map<number, number>;
  /** Category parents only: the category's title, for the Home-row preview. */
  parentTitle?: string;
}

const HIGHLIGHT_MS = 2500;

/**
 * Orders a parent's children by drag and drop (pointer or keyboard) with
 * staged Save/Discard. A Root orders categories; a Category orders series
 * AND directly placed videos/streams in one sequence (ADR-0014 D2: one
 * position space, one order — a series is a tile that opens, an item is a
 * tile that plays) and previews the Home row it produces.
 */
export function OrderedChildrenEditor({
  parentLlId,
  parentType,
  orderedChildren,
  allAvailableChildren,
  childLookup,
  childDetailRoute,
  childViews,
  seasonCountByLlId,
  parentTitle,
}: OrderedChildrenEditorProps) {
  const navigate = useNavigate();
  const addMutation = useAddChild();
  const addManyMutation = useAddChildren();
  const removeMutation = useRemoveChild();
  const saveOrderMutation = useSaveChildOrder();

  const isCategory = parentType === ListlistTypeId.Category;
  const childTypeLabel = AppContentUtils.getChildTypeLabel(parentType);
  const childTypeLabelPlural = AppContentUtils.getChildTypeLabelPlural(parentType);

  // Local draft state for ordering (staged Save/Discard)
  const [draftOrder, setDraftOrder] = useState<ChildListlistOrListId[]>(orderedChildren);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Keys of the rows just added: highlighted briefly, and the first one scrolled into view.
  const [recentKeys, setRecentKeys] = useState<Set<string>>(() => new Set());
  const listRef = useRef<HTMLDivElement>(null);

  // Sync draft when server data changes
  useEffect(() => {
    setDraftOrder(orderedChildren);
  }, [orderedChildren]);

  const draftKeys = useMemo(() => draftOrder.map(AppContentUtils.childKey), [draftOrder]);
  const serverKeys = useMemo(() => orderedChildren.map(AppContentUtils.childKey), [orderedChildren]);

  // Check if draft differs from server state
  const hasOrderChanges = useMemo(() => {
    if (draftKeys.length !== serverKeys.length) return false;
    return draftKeys.some((key, i) => key !== serverKeys[i]);
  }, [draftKeys, serverKeys]);

  const placedKeys = useMemo(() => new Set(draftKeys), [draftKeys]);
  const placedLlIds = useMemo(
    () => draftOrder.flatMap((c) => (c.childLLId != null ? [c.childLLId] : [])),
    [draftOrder],
  );

  // Once the server order carries the new rows, scroll the first one into view and let the highlight fade.
  useEffect(() => {
    if (recentKeys.size === 0) return;
    const first = serverKeys.find((k) => recentKeys.has(k));
    if (first) {
      const el = listRef.current?.querySelector<HTMLElement>(`[data-child-key="${CSS.escape(first)}"]`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    const timer = window.setTimeout(() => setRecentKeys(new Set()), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [serverKeys, recentKeys]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const findRef = useCallback(
    (key: string) => draftOrder.find((c) => AppContentUtils.childKey(c) === key),
    [draftOrder],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = draftKeys.indexOf(String(active.id));
      const newIndex = draftKeys.indexOf(String(over.id));

      if (oldIndex !== -1 && newIndex !== -1) {
        setDraftOrder(arrayMove(draftOrder, oldIndex, newIndex));
      }
    },
    [draftOrder, draftKeys],
  );

  // Root (and any non-category parent): the single-listlist dialog, unchanged.
  const handleAdd = useCallback(
    (childLlId: number) => {
      addMutation.mutate(
        { parentLLId: parentLlId, childId: AppContentUtils.llRef(childLlId) },
        {
          onSuccess: () => {
            setAddDialogOpen(false);
            setRecentKeys(new Set([AppContentUtils.childKey(AppContentUtils.llRef(childLlId))]));
            notifications.show({
              title: 'Added',
              message: `${childTypeLabel} added.`,
              color: 'green',
            });
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        },
      );
    },
    [parentLlId, addMutation, childTypeLabel],
  );

  // Category: the one picker for series, videos and streams.
  const handleAddPicks = useCallback(
    async (picks: ChildListlistOrListId[], kinds: ChildKind[], position: AddPosition) => {
      try {
        const placed = await addManyMutation.mutateAsync({
          parentLLId: parentLlId,
          childIds: picks,
          placeAtTop: position === 'top',
          currentOrder: orderedChildren,
        });
        setPickerOpen(false);
        setRecentKeys(new Set(placed.map(AppContentUtils.childKey)));
        notifications.show({
          title: 'Added',
          message: `${AppContentUtils.addedToastMessage(kinds.slice(0, placed.length))} to “${parentTitle ?? 'the category'}”.`,
          color: 'green',
        });
      } catch (err) {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Could not add to the category.',
          color: 'red',
        });
      }
    },
    [parentLlId, addManyMutation, orderedChildren, parentTitle],
  );

  const handleRemove = useCallback(
    (key: string) => {
      const ref = findRef(key);
      if (!ref) return;
      const view = childViews?.get(key);
      const label = view ? CHILD_KIND_LABELS[view.kind] : AppContentUtils.getChildRefLabel(parentType, ref);
      const title =
        view?.title ??
        (ref.childLLId != null ? childLookup.get(ref.childLLId)?.llr.title : undefined) ??
        'this item';
      const keepsNoun = label.toLowerCase();

      modals.openConfirmModal({
        title: `Remove ${label.toLowerCase()} from this ${isCategory ? 'category' : childTypeLabel.toLowerCase() === 'category' ? 'content group' : 'list'}`,
        children: (
          <Text size="sm">
            Remove &ldquo;{title}&rdquo;? The {keepsNoun} itself is not deleted — it stays in your library.
          </Text>
        ),
        labels: { confirm: 'Remove', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          removeMutation.mutate(
            { parentLLId: parentLlId, childId: ref },
            {
              onSuccess: () => {
                // Remove from local draft immediately
                setDraftOrder((prev) => prev.filter((c) => !AppContentUtils.isSameChild(c, ref)));
                notifications.show({
                  title: 'Removed',
                  message: `Removed “${title}”.`,
                  color: 'green',
                });
              },
              onError: (err) => {
                notifications.show({ title: 'Error', message: err.message, color: 'red' });
              },
            },
          );
        },
      });
    },
    [parentLlId, parentType, childLookup, childViews, findRef, removeMutation, isCategory, childTypeLabel],
  );

  const handleSaveOrder = useCallback(() => {
    saveOrderMutation.mutate(
      { parentLLId: parentLlId, childIds: draftOrder },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Saved',
            message: 'Order saved.',
            color: 'green',
          });
        },
        onError: (err) => {
          notifications.show({ title: 'Error', message: err.message, color: 'red' });
        },
      },
    );
  }, [parentLlId, draftOrder, saveOrderMutation]);

  const handleDiscardOrder = useCallback(() => {
    setDraftOrder(orderedChildren);
  }, [orderedChildren]);

  const handleNavigate = useCallback(
    (key: string) => {
      const view = childViews?.get(key);
      if (view?.href) {
        navigate({ to: view.href as string });
        return;
      }
      const ref = findRef(key);
      if (ref?.childLLId == null) return;
      const displayId = AppContentUtils.getDisplayId(ref.childLLId);
      navigate({ to: `${childDetailRoute}/${displayId}` as string });
    },
    [navigate, childDetailRoute, findRef, childViews],
  );

  // The Home-row preview follows the DRAFT order, so a drag shows its effect before Save.
  const previewChildren = useMemo<CategoryChildView[]>(() => {
    if (!isCategory || !childViews) return [];
    return draftKeys.flatMap((k) => {
      const v = childViews.get(k);
      return v ? [v] : [];
    });
  }, [isCategory, childViews, draftKeys]);

  const openAdd = () => (isCategory ? setPickerOpen(true) : setAddDialogOpen(true));
  // A category's button names what it adds (series, videos, live streams = the
  // header's "Items"); "Add to category" read as "add a category" (owner, 2026-09-24).
  const addLabel = isCategory ? CATEGORY_ADD_LABEL : `Add ${childTypeLabel}`;

  return (
    <Stack gap="md">
      {/* Sticky header: stays visible while a long list scrolls, so Save Order is never out of reach */}
      <Box
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          backgroundColor: 'var(--mantine-color-body)',
          paddingBlock: 8,
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Box>
            <Text fw={600} size="lg">
              {childTypeLabelPlural} ({draftOrder.length})
            </Text>
            {isCategory && draftOrder.length > 0 && (
              <Text size="xs" c="dimmed">
                {hasOrderChanges ? 'Order changed — save to publish it to Home.' : 'This order is the Home row order.'}
              </Text>
            )}
          </Box>
          <Group gap="sm" wrap="nowrap">
            {hasOrderChanges && (
              <>
                <Button variant="default" size="sm" onClick={handleDiscardOrder}>
                  Discard
                </Button>
                <Button size="sm" onClick={handleSaveOrder} loading={saveOrderMutation.isPending}>
                  Save order
                </Button>
              </>
            )}
            <Button leftSection={<IconPlus size={16} />} size="sm" onClick={openAdd}>
              {addLabel}
            </Button>
          </Group>
        </Group>
      </Box>

      {isCategory && previewChildren.length > 0 && (
        <HomeRowPreview title={parentTitle ?? ''} children={previewChildren} />
      )}

      {draftOrder.length === 0 ? (
        <Card withBorder p="xl">
          <Stack align="center" gap="sm">
            <IconListTree size={36} stroke={1.5} opacity={0.4} />
            <Text size="sm" c="dimmed" ta="center" maw={440}>
              {isCategory
                ? 'This category is empty. Add series, videos or live streams — they appear on Home as tiles in this order.'
                : `No ${childTypeLabelPlural.toLowerCase()} assigned yet.`}
            </Text>
            <Button leftSection={<IconPlus size={14} />} size="sm" onClick={openAdd}>
              {addLabel}
            </Button>
          </Stack>
        </Card>
      ) : (
        <Card withBorder p="sm" ref={listRef}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={draftKeys}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap={6}>
                {draftOrder.map((ref, index) => {
                  const key = draftKeys[index];
                  const view = childViews?.get(key);
                  if (isCategory && view) {
                    return (
                      <SortableChildItem
                        key={key}
                        id={key}
                        index={index}
                        title={view.title}
                        subtitle={view.subtitle}
                        kind={view.kind}
                        thumbnailUrl={view.thumbnailUrl}
                        kindLabel={CHILD_KIND_LABELS[view.kind]}
                        kindColor={CHILD_KIND_COLORS[view.kind]}
                        onAir={view.isOnAir}
                        inactive={!view.isActive}
                        unavailable={view.unavailable}
                        highlighted={recentKeys.has(key)}
                        onRemove={handleRemove}
                        onNavigate={view.href ? handleNavigate : undefined}
                      />
                    );
                  }
                  const llId = ref.childLLId;
                  const child = llId != null ? childLookup.get(llId) : undefined;
                  return (
                    <SortableChildItem
                      key={key}
                      id={key}
                      index={index}
                      title={child?.llr.title ?? `Unknown (${llId ?? ref.childListId ?? ref.childMediaId})`}
                      displayId={llId != null ? AppContentUtils.getDisplayId(llId) : undefined}
                      highlighted={recentKeys.has(key)}
                      onRemove={handleRemove}
                      onNavigate={llId != null ? handleNavigate : undefined}
                    />
                  );
                })}
              </Stack>
            </SortableContext>
          </DndContext>
          <Text size="xs" c="dimmed" mt="xs" ta="center">
            Drag to reorder, or focus a handle and use Space and the arrow keys
          </Text>
        </Card>
      )}

      {!isCategory && (
        <AddChildDialog
          opened={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onAdd={handleAdd}
          availableChildren={allAvailableChildren}
          existingChildIds={placedLlIds}
          childTypeLabel={childTypeLabel}
          isPending={addMutation.isPending}
        />
      )}

      {isCategory && (
        <AddToCategoryDialog
          opened={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onAdd={handleAddPicks}
          availableSeries={allAvailableChildren}
          seasonCountByLlId={seasonCountByLlId ?? new Map()}
          placedKeys={placedKeys}
          categoryTitle={parentTitle}
          isPending={addManyMutation.isPending}
        />
      )}
    </Stack>
  );
}
